import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { Snapshot } from '../../types/snapshot.types';
import { useAppQuery } from '../use-app-query.hook';

// Full-stats detail fetch for one snapshot. The list endpoints return slim rows (useSnapshots),
// so pages resolve the selected row's id from the list and load the full grid here on demand.
export const useSnapshot = ({
  companyId,
  snapshotId,
  capTableMode,
}: {
  capTableMode?: boolean;
  companyId: string;
  snapshotId: string;
}) => {
  const queryClient = useQueryClient();

  const { data: snapshot, isLoading } = useAppQuery<Snapshot>({
    // capTableMode is part of the key: `/snapshots/:id` and `/cap-table-snapshots/:id` read
    // different tables (same as the list hooks).
    queryKey: [QueryKey.GET_SNAPSHOT, companyId, snapshotId, Boolean(capTableMode)],
    queryFn: () =>
      ApiService.get({
        endpoint: BackendRoute.COMPANIES,
        routePath: [companyId, capTableMode ? 'cap-table-snapshots' : 'snapshots', snapshotId],
      }),
    enabled: !!companyId && !!snapshotId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SNAPSHOT] });
  }, [queryClient]);

  return {
    invalidateQuery,
    snapshot,
    isLoading,
  };
};
